import { type ReactElement, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { ProtectedRoute, useSetupGuard } from '@gastrosoft/shared/auth';
import { authRoles } from '@gastrosoft/shared/utils';
import { LoadingIndicator } from '@gastrosoft/shared/ui';
import { useThemeSwitcher } from '@gastrosoft/shared/theme';
import { NotificationProvider } from '@/app/contexts/NotificationContext';
import { useOrganization } from '@/app/contexts/OrganizationContext';
import { useSetup } from '@/app/contexts/SetupContext';
import Login from '@/app/auth/Login';
import Register from '@/app/auth/Register';
import ForgotPassword from '@/app/auth/ForgotPassword';
import ResetPassword from '@/app/auth/ResetPassword';
import NotAReseller from '@/app/auth/NotAReseller';
import MainLayout from '@/app/layouts/MainLayout';
import SetupWorkflow from '@/app/modules/setup/SetupWorkflow';
import Dashboard from '@/app/modules/dashboard/Dashboard';
import Customers from '@/app/modules/customers/Customers';
import TenantDetail from '@/app/modules/customers/TenantDetail';
import Invitations from '@/app/modules/invitations/Invitations';
import Resellers from '@/app/modules/resellers/Resellers';
import Licences from '@/app/modules/licences/Licences';
import Subscriptions from '@/app/modules/subscriptions/Subscriptions';
import SubscriptionDetail from '@/app/modules/subscriptions/SubscriptionDetail';
import Invoices from '@/app/modules/invoices/Invoices';
import AccountingDocumentDetail from '@/app/modules/invoices/AccountingDocumentDetail';
import Payments from '@/app/modules/payments/Payments';
import PaymentDetail from '@/app/modules/payments/PaymentDetail';
import OrganizationDetail from '@/app/modules/organizations/OrganizationDetail';
import OrganizationUnits from '@/app/modules/organization-units/OrganizationUnits';
import OrganizationUnitDetail from '@/app/modules/organization-units/OrganizationUnitDetail';
import OrganizationAddresses from '@/app/modules/organization-addresses/OrganizationAddresses';
import OrganizationAddressDetail from '@/app/modules/organization-addresses/OrganizationAddressDetail';
import OrganizationContacts from '@/app/modules/organization-contacts/OrganizationContacts';
import OrganizationContactDetail from '@/app/modules/organization-contacts/OrganizationContactDetail';
import PaymentGateways from '@/app/modules/payment-gateways/PaymentGateways';
import PaymentGatewayDetail from '@/app/modules/payment-gateways/PaymentGatewayDetail';
import PaymentTerms from '@/app/modules/payment-terms/PaymentTerms';
import PaymentTermDetail from '@/app/modules/payment-terms/PaymentTermDetail';
import Plans from '@/app/modules/plans/Plans';
import PlanDetail from '@/app/modules/plans/PlanDetail';
import Products from '@/app/modules/products/Products';
import ProductDetail from '@/app/modules/products/ProductDetail';
import TaxRules from '@/app/modules/tax-rules/TaxRules';
import TaxRuleDetail from '@/app/modules/tax-rules/TaxRuleDetail';
import PaymentAccountDetail from '@/app/modules/payment-accounts/PaymentAccountDetail';
import LicenceDetail from '@/app/modules/licences/LicenceDetail';
import Logs from '@/app/modules/system/Logs';
import Webhooks from '@/app/modules/system/Webhooks';
import AppVersions from '@/app/modules/system/AppVersions';
import Unauthorized from '@/app/pages/Unauthorized';
import SecurityDevices from './modules/securityDevices/SecurityDevices';
import Organizations from './modules/reseller-organizations/Organizations';
import Coupons from './modules/coupons/Coupons';
import CouponDetail from './modules/coupons/CouponDetail';
import Resources from './modules/resources/Resources';
import AvailableCoupons from './modules/coupons/AvailableCoupons';

function SuperAdminRoute({ children }: { children: ReactNode }) {
  return <ProtectedRoute allowedRoles={authRoles.superAdmin}>{children}</ProtectedRoute>;
}

/** Blocks access until the tenant has been activated as a reseller (type RESELLER + resellerId). */
function useResellerAccessGuard(): ReactElement | null {
  const { hasResellerAccess, loading } = useOrganization();
  if (loading) {
    return <LoadingIndicator fullScreen />;
  }
  if (!hasResellerAccess) {
    return <NotAReseller />;
  }
  return null;
}

/** Redirects to/from the setup wizard depending on whether the reseller still needs it. */
function AppProtectedRoute({
  children,
  allowedRoles,
}: {
  children: ReactNode;
  allowedRoles?: string[];
}) {
  const { currentBrand } = useThemeSwitcher();
  const { needsSetup, checkingSetup } = useSetup();
  const resellerAccessGuardElement = useResellerAccessGuard();
  const setupGuardElement = useSetupGuard({
    needsSetup,
    checkingSetup,
    hasNetworkError: false,
    setupEnabled: currentBrand === 'orda-pos',
    setupPath: '/setup',
    dashboardPath: '/dashboard',
  });
  const guardElement = resellerAccessGuardElement ?? setupGuardElement;
  return (
    <ProtectedRoute allowedRoles={allowedRoles} guardElement={guardElement}>
      {children}
    </ProtectedRoute>
  );
}

function AppRouteLayout() {
  const { theme } = useThemeSwitcher();
  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline enableColorScheme />
      <NotificationProvider>
        <Outlet />
      </NotificationProvider>
    </ThemeProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <AppRouteLayout />,
    children: [
      { path: 'login', element: <Login /> },
      { path: 'register', element: <Register /> },
      { path: 'forgotPassword', element: <ForgotPassword /> },
      { path: 'reset-password', element: <ResetPassword /> },
      { path: 'unauthorized', element: <Unauthorized /> },
      {
        path: '/setup',
        element: (
          <AppProtectedRoute>
            <SetupWorkflow />
          </AppProtectedRoute>
        ),
      },
      {
        path: '/',
        element: (
          <AppProtectedRoute allowedRoles={['Super Admin', 'Admin', 'Reseller']}>
            <MainLayout />
          </AppProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: <Dashboard /> },
          { path: 'customers', element: <Customers /> },
          { path: 'customers/:id', element: <TenantDetail /> },
          { path: 'invitations', element: <Invitations /> },
          { path: 'resellers', element: <Resellers /> },
          { path: 'licences', element: <Licences /> },
          { path: 'licences/:id', element: <LicenceDetail /> },
          { path: 'subscriptions', element: <Subscriptions /> },
          { path: 'subscriptions/:id', element: <SubscriptionDetail /> },
          { path: 'invoices', element: <Invoices /> },
          { path: 'invoices/:id', element: <AccountingDocumentDetail /> },
          { path: 'payments', element: <Payments /> },
          { path: 'payments/:id', element: <PaymentDetail /> },
          { path: 'available-coupons', element: <AvailableCoupons /> },
          { path: 'resources', element: <Resources /> },
          {
            path: 'security-devices',
            element: (
              <SuperAdminRoute>
                <SecurityDevices />
              </SuperAdminRoute>
            ),
          },
          { path: 'organizations/:id', element: <OrganizationDetail /> },
          { path: 'organization-units', element: <OrganizationUnits /> },
          { path: 'organization-units/:id', element: <OrganizationUnitDetail /> },
          { path: 'organization-addresses', element: <OrganizationAddresses /> },
          { path: 'organization-addresses/:id', element: <OrganizationAddressDetail /> },
          { path: 'organization-contacts', element: <OrganizationContacts /> },
          { path: 'organization-contacts/:id', element: <OrganizationContactDetail /> },
          {
            path: 'payment-gateways',
            element: (
              <SuperAdminRoute>
                <PaymentGateways />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'payment-gateways/:id',
            element: (
              <SuperAdminRoute>
                <PaymentGatewayDetail />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'payment-terms',
            element: (
              <SuperAdminRoute>
                <PaymentTerms />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'payment-terms/:id',
            element: (
              <SuperAdminRoute>
                <PaymentTermDetail />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'plans',
            element: (
              <SuperAdminRoute>
                <Plans />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'plans/:id',
            element: (
              <SuperAdminRoute>
                <PlanDetail />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'products',
            element: (
              <SuperAdminRoute>
                <Products />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'products/:id',
            element: (
              <SuperAdminRoute>
                <ProductDetail />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'tax-rules',
            element: (
              <SuperAdminRoute>
                <TaxRules />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'tax-rules/:id',
            element: (
              <SuperAdminRoute>
                <TaxRuleDetail />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'coupons',
            element: (
              <SuperAdminRoute>
                <Coupons />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'coupons/:id',
            element: (
              <SuperAdminRoute>
                <CouponDetail />
              </SuperAdminRoute>
            ),
          },
          { path: 'payment-accounts/:id', element: <PaymentAccountDetail /> },
          {
            path: 'system/logs',
            element: (
              <SuperAdminRoute>
                <Logs />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'system/webhooks',
            element: (
              <SuperAdminRoute>
                <Webhooks />
              </SuperAdminRoute>
            ),
          },
          {
            path: 'system/versions',
            element: (
              <SuperAdminRoute>
                <AppVersions />
              </SuperAdminRoute>
            ),
          },
          { path: 'reseller-organizations', element: <Organizations /> },
          { path: 'reseller-organizations/:id', element: <OrganizationDetail /> },
        ],
      },
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);
